
  import {defineComponent, onMounted, reactive, toRefs, watch} from "vue";
  import {isAuthorized, t} from "@/mixin/mixins";
  import {EmployeeCountingPerformance} from "@/model/EmployeeCountingPerformance";
  import StatisticService, {CountingDto, OperatorPerformanceResponse} from "@/service/StatisticService";
  import {employeeUuid as storeEmployeeUuid} from "@/store/StoreUtils";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import MonthPicker from "@/components/MonthPicker.vue";
  import router from "@/router";
  import moment from "moment";

  export default defineComponent({
    name: "InventoriesCountingPerformance",
    components: {
      MonthPicker
    },
    setup() {
      const initialState = {
        yearMonth: null as unknown as string,
        countingPerformances: new Array<EmployeeCountingPerformance>(),
        totalPerformance: {} as EmployeeCountingPerformance
      };
      const state = reactive({...initialState});

      watch(() => state.yearMonth, (newValue) => {
        refreshData();
        router.push({
          query: Object.assign({}, router.currentRoute.query, {date: newValue}),
          replace: true
        }).catch(() => {
        });
      });

      const headers = [
        {
          text: t("inventories.countingPerformance.table.header.date"),
          value: "date"
        },
        {
          text: t("inventories.countingPerformance.table.header.name"),
          value: "name"
        },
        {
          text: t("inventories.countingPerformance.table.header.qualityLevel"),
          value: "qualityLevel"
        },
        {
          text: t("inventories.countingPerformance.table.header.workingHours"),
          value: "workingMinutes"
        },
        {
          text: t("inventories.countingPerformance.table.header.positions"),
          value: "positions"
        },
        {
          text: t("inventories.countingPerformance.table.header.positionsPerHour"),
          value: "positionsPerHour"
        },
        {
          text: t("inventories.countingPerformance.table.header.positionsPerHourInventory"),
          value: "positionsPerHourInventory"
        }
      ];

      onMounted(() => {
        state.yearMonth = moment().format("YYYY-MM");
      });

      const refreshData = () => {
        const employeeUuid = getEmployeeUuid();
        if (employeeUuid) loadCountingPerformancesOfEmployee(employeeUuid);
      };

      /**
       * The statistics overview links here with the uuid of the employee to show; otherwise it is the logged-in one.
       */
      const getEmployeeUuid = (): string | undefined => {
        const employeeUuidParam = router.currentRoute.query.employeeUuid?.toString();
        if (employeeUuidParam && isAuthorized("Statistic")) {
          return employeeUuidParam;
        } else {
          return storeEmployeeUuid.value;
        }
      };

      const loadCountingPerformancesOfEmployee = (employeeUuid: string) => {
        if (employeeUuid && state.yearMonth) {
          StatisticService.getOperatorPerformance(employeeUuid, state.yearMonth)
            .then(response => {
              state.countingPerformances = toCountingPerformances(response);
              state.totalPerformance = toTotalPerformance(response);
            })
            .catch(() => {
              eventBus.$emit(MessageTypes.ERROR, t("errors.generalError"));
            });
        }
      };

      const toCountingPerformances = (response: OperatorPerformanceResponse): Array<EmployeeCountingPerformance> => {
        return response.orders.map(o => ({
          id: o.order.id,
          date: o.order.validFrom,
          name: o.order.name,
          qualityLevel: o.qualityLevel,
          workingMinutes: o.counting.workedMinutes,
          positions: o.counting.positions,
          positionsPerHour: roundedPositionsPerHour(o.counting),
          positionsPerHourInventory: roundedPositionsPerHour(o.orderTotal)
        }));
      };

      /**
       * The employee's total comes from the API; the inventories' total is calculated from their summed positions and
       * minutes, as the API does for the employee's.
       */
      const toTotalPerformance = (response: OperatorPerformanceResponse): EmployeeCountingPerformance => {
        const totalWorkingMinutes = response.orders.reduce((a, b) => a + (b.orderTotal.workedMinutes ?? 0), 0);
        const totalPositions = response.orders.reduce((a, b) => a + (b.orderTotal.positions ?? 0), 0);
        return {
          workingMinutes: response.performance.total.workedMinutes,
          positions: response.performance.total.positions,
          positionsPerHour: roundedPositionsPerHour(response.performance.total),
          positionsPerHourInventory: positionsPerHour(totalWorkingMinutes, totalPositions)
        };
      };

      const roundedPositionsPerHour = (counting: CountingDto) => {
        return counting.positionsPerHour ? Math.round(counting.positionsPerHour) : 0;
      };

      const positionsPerHour = (minutes: number | undefined, positions: number | undefined) => {
        if (minutes && minutes > 0) {
          return Math.round(positions ? positions / (minutes / 60) : 0);
        } else {
          return 0;
        }
      };

      const toTimeFormat = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const hour = h < 10 ? "0" + h : h;
        const minute = m < 10 ? "0" + m : m;
        return hour + ":" + minute;
      };

      return {
        ...toRefs(state),
        headers,
        positionsPerHour,
        toTimeFormat
      };
    }
  });
